import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {fetchProductByID, fetchProductsSummary} from "../../../../../../reduxLib/productLib";
import {fetchColorsByTextiles} from "../../../../../../reduxLib/colorsLib";
import { fetchShoppingCardById } from "../../../../../../reduxLib/shoppingcardLib";


const TEXTILES_OPTIONS = [
    { value: "bambakero", label: "Βαμβακερό" },
    { value: "bambakeroLourex", label: "Βαμβακερό Λούρεξ" },
    { value: "forte", label: "Forte" },
];

const SIZE_OPTIONS = ["Small", "Medium", "Large", "One Size", "SpecialCase", "S", "M", "L"];

const PLEXI_OPTIONS = [
    { value: "none", label: "Χωρίς πλέξη" },
    { value: "basic", label: "Basic" },
    { value: "grammh", label: "Γραμμή" },
];

const CLOSING_OPTIONS = [
    { value: "None", label: "Χωρίς κλείσιμο" },
    { value: "SIMPLE", label: "Απλό" },
    { value: "BUTTON", label: "Κουμπί" },
];

const getColorId = (color) => color?.pcid ?? "";
const getColorName = (color) => color?.colorName ?? color?.ColorName ?? "Χρώμα";
const getColorHex = (color) => color?.hexColor ?? color?.HexColor ?? "#ffffff";

const normalizeProduct = (product) => ({
    ...product,
    quantity: product.quantity ?? 1,

    size: product.size ?? product.productSize ?? "One Size",
    textiles: product.textiles ?? "bambakero",
    colorId: product.colorId ?? product.colors?.[0]?.pcid ?? "",
    colorName: product.colorName ?? getColorName(product.colors?.[0]) ?? "",
    availableColors: product.colors ?? [],

    plexis: product.plexis ?? "none",
    closingTextiles: product.closingTextiles ?? "None",

    imageURL:
        product.imageURL ||
        product.filesRelated?.[0]?.fileLocation ||
        product.filesRelated?.[0]?.FileLocation ||
        "",
});

function EditModal({ isOpen, onClose, order, onSave }) {
    const dispatch = useDispatch();

    // άλλαξε selector αν το slice σου λέγεται αλλιώς


    const [orderStatus, setOrderStatus] = useState("");
    const [editableProducts, setEditableProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState("");
    const [isAddingProduct, setIsAddingProduct] = useState(false);
    const [originalProducts, setOriginalProducts] = useState([]);


    const productsSummary = useSelector(
        (state) => state.productreducer.productSummaryList || []
    );

    useEffect(() => {
        if (!isOpen || !order) return;

        const loadOrderData = async () => {
            setOrderStatus(order.orderStatus || "");

            dispatch(fetchProductsSummary());

            try {
                const card = await dispatch(
                    fetchShoppingCardById(order.shopingCardIdRel)
                ).unwrap();

                const productsFromCard = (card.productItems || []).map((item) =>
                    normalizeProduct({
                        ...item.product,
                        quantity: item.quantity,
                        cardItemId: item.cardItemId,
                    })
                );

                setEditableProducts(productsFromCard);
                setOriginalProducts(productsFromCard);
            } catch (error) {
                console.error("Failed to load shopping card", error);
                setEditableProducts([]);
            }
        };

        loadOrderData();
    }, [isOpen, order, dispatch]);

    const toCardItemCreator = (product) => ({
        productId: product.productID,
        productName: product.name,
        image: product.imageURL || "",
        price: Number(product.price || 0),
        quantity: Number(product.quantity || 1),
        colorId: Number(product.colorId || 0),
        textiles: product.textiles,
        closingTextiles: product.closingTextiles,
        plexis: product.plexis,
        categoryRelId: product.categoryRelId ?? 0,
    });

    const total = useMemo(() => {
        return editableProducts.reduce((sum, product) => {
            return sum + Number(product.price || 0) * Number(product.quantity || 1);
        }, 0);
    }, [editableProducts]);

    const numProducts = useMemo(() => {
        return editableProducts.reduce((sum, product) => {
            return sum + Number(product.quantity || 1);
        }, 0);
    }, [editableProducts]);

    if (!isOpen || !order) return null;

    const updateProduct = async (index, field, value) => {
        if (field === "textiles") {
            const colors = await dispatch(fetchColorsByTextiles(value)).unwrap();

            setEditableProducts((prev) =>
                prev.map((product, productIndex) => {
                    if (productIndex !== index) return product;

                    return {
                        ...product,
                        textiles: value,
                        colorId: "",
                        colorName: "",
                        availableColors: colors || [],
                    };
                })
            );

            return;
        }

        setEditableProducts((prev) =>
            prev.map((product, productIndex) => {
                if (productIndex !== index) return product;

                const updatedProduct = {
                    ...product,
                    [field]: value,
                };

                if (field === "colorId") {
                    const selectedColor = product.availableColors?.find(
                        (color) => String(getColorId(color)) === String(value)
                    );

                    updatedProduct.colorName = selectedColor
                        ? getColorName(selectedColor)
                        : "";
                }

                return updatedProduct;
            })
        );
    };

    const addProduct = async () => {
        if (!selectedProductId) return;

        try {
            setIsAddingProduct(true);

            const fullProduct = await dispatch(
                fetchProductByID(selectedProductId)
            ).unwrap();

            setEditableProducts((prev) => [
                ...prev,
                normalizeProduct(fullProduct),
            ]);

            setSelectedProductId("");
        } finally {
            setIsAddingProduct(false);
        }
    };

    const removeProduct = (index) => {
        setEditableProducts((prev) =>
            prev.filter((_, productIndex) => productIndex !== index)
        );
    };

    const handleSave = () => {
        const originalByCardItemId = new Map(
            originalProducts
                .filter((product) => product.cardItemId)
                .map((product) => [product.cardItemId, product])
        );

        const currentByCardItemId = new Map(
            editableProducts
                .filter((product) => product.cardItemId)
                .map((product) => [product.cardItemId, product])
        );

        const removed = originalProducts.filter(
            (product) =>
                product.cardItemId &&
                !currentByCardItemId.has(product.cardItemId)
        );

        const added = editableProducts.filter(
            (product) => !product.cardItemId
        );

        const updated = editableProducts.filter((product) => {
            if (!product.cardItemId) return false;

            const oldProduct = originalByCardItemId.get(product.cardItemId);
            if (!oldProduct) return false;

            return (
                Number(oldProduct.quantity) !== Number(product.quantity) ||
                Number(oldProduct.colorId) !== Number(product.colorId) ||
                oldProduct.textiles !== product.textiles ||
                oldProduct.closingTextiles !== product.closingTextiles ||
                oldProduct.plexis !== product.plexis
            );
        });

        onSave?.({
            shoppingCardId: order.shopingCardIdRel,
            orderStatus,
            added: added.map(toCardItemCreator),
            updated: updated.map((product) => ({
                cardItemId: product.cardItemId,
                item: toCardItemCreator(product),
            })),
            removed: removed.map((product) => product.cardItemId),
        });

        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
                <div className="border-b px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-custom-green">
                        Edit Order #{order.orderId}
                    </h2>

                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Order Status
                        </label>

                        <select
                            value={orderStatus}
                            onChange={(e) => setOrderStatus(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                        >
                            <option value="pending">Processing</option>
                            <option value="placed">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="shipped">Shipped</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-bold text-gray-800 mb-3">
                            Add Product
                        </h3>

                        <div className="flex gap-3">
                            <select
                                value={selectedProductId}
                                onChange={(e) => setSelectedProductId(e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white"
                            >
                                <option value="">Select product</option>

                                {productsSummary.map((product) => (
                                    <option
                                        key={product.productID}
                                        value={product.productID}
                                    >
                                        {product.name} - €{product.price}
                                    </option>
                                ))}
                            </select>

                            <button
                                type="button"
                                onClick={addProduct}
                                disabled={!selectedProductId || isAddingProduct}
                                className="px-4 py-2 bg-custom-green text-white rounded-lg disabled:opacity-50"
                            >
                                {isAddingProduct ? "Adding..." : "Add"}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="font-bold text-gray-800">Products</h3>

                        {editableProducts.map((product, index) => (
                            <div key={`${product.productID}-${index}`} className="border rounded-lg p-4">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                    <div className="md:col-span-2">
                                        {product.imageURL ? (
                                            <img
                                                src={product.imageURL}
                                                alt={product.name}
                                                className="w-full h-28 object-cover rounded-lg bg-gray-100"
                                            />
                                        ) : (
                                            <div className="w-full h-28 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                                                No image
                                            </div>
                                        )}
                                    </div>

                                    <div className="md:col-span-10 space-y-4">
                                        <div className="flex justify-between gap-4">
                                            <div>
                                                <h4 className="font-bold text-gray-800">
                                                    {product.name}
                                                </h4>

                                                <p className="text-sm text-gray-500">
                                                    Product ID: {product.productID}
                                                </p>

                                                <p className="text-sm font-medium text-custom-green">
                                                    €{product.price}
                                                </p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => removeProduct(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Quantity
                                                </label>

                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={product.quantity}
                                                    onChange={(e) =>
                                                        updateProduct(
                                                            index,
                                                            "quantity",
                                                            Math.max(1, Number(e.target.value))
                                                        )
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Size
                                                </label>

                                                <select
                                                    value={product.size}
                                                    onChange={(e) =>
                                                        updateProduct(index, "size", e.target.value)
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    {SIZE_OPTIONS.map((size) => (
                                                        <option key={size} value={size}>
                                                            {size}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Νήμα
                                                </label>

                                                <select
                                                    value={product.textiles}
                                                    onChange={(e) =>
                                                        updateProduct(index, "textiles", e.target.value)
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    {TEXTILES_OPTIONS.map((textile) => (
                                                        <option key={textile.value} value={textile.value}>
                                                            {textile.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Χρώμα
                                                </label>

                                                <select
                                                    value={product.colorId}
                                                    onChange={(e) =>
                                                        updateProduct(index, "colorId", e.target.value)
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    <option value="">Select color</option>

                                                    {(product.availableColors || []).map((color) => (
                                                        <option
                                                            key={getColorId(color)}
                                                            value={getColorId(color)}
                                                        >
                                                            {getColorName(color)}
                                                        </option>
                                                    ))}
                                                </select>

                                                {product.colorId && (
                                                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                                                        <span
                                                            className="w-4 h-4 rounded-full border"
                                                            style={{
                                                                backgroundColor: getColorHex(
                                                                    product.availableColors?.find(
                                                                        (color) =>
                                                                            String(getColorId(color)) ===
                                                                            String(product.colorId)
                                                                    )
                                                                ),
                                                            }}
                                                        />

                                                        <span>{product.colorName}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Πλέξη
                                                </label>

                                                <select
                                                    value={product.plexis}
                                                    onChange={(e) =>
                                                        updateProduct(index, "plexis", e.target.value)
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    {PLEXI_OPTIONS.map((plexi) => (
                                                        <option key={plexi.value} value={plexi.value}>
                                                            {plexi.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Κλείσιμο
                                                </label>

                                                <select
                                                    value={product.closingTextiles}
                                                    onChange={(e) =>
                                                        updateProduct(
                                                            index,
                                                            "closingTextiles",
                                                            e.target.value
                                                        )
                                                    }
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                >
                                                    {CLOSING_OPTIONS.map((closing) => (
                                                        <option key={closing.value} value={closing.value}>
                                                            {closing.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="text-right text-sm text-gray-600">
                                            Line total:{" "}
                                            <strong>
                                                €
                                                {Number(product.price || 0) *
                                                    Number(product.quantity || 1)}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border-t pt-4 text-right">
                        <p className="text-sm text-gray-600">
                            Total Products: <strong>{numProducts}</strong>
                        </p>

                        <p className="text-lg font-bold text-custom-green">
                            Total: €{total}
                        </p>
                    </div>
                </div>

                <div className="border-t px-6 py-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-4 py-2 bg-custom-green text-white rounded-lg hover:bg-opacity-90"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EditModal;